import React, { useState } from "react";
import { motion } from "framer-motion";
import { SectionFrame } from "../atoms/SectionFrame";
import { GlassCard } from "../atoms/GlassCard";
import { AssetPlaceholder } from "../atoms/AssetPlaceholder";
import { ASSETS } from "../../lib/assets";
import { TID } from "../../lib/testIds";

const SERVICES = [
    {
        number: "01",
        name: "Product Stories",
        line: "Turn complex products into stories people instantly get.",
    },
    {
        number: "02",
        name: "Learning Experiences",
        line: "Training people actually finish and remember.",
    },
    {
        number: "03",
        name: "Internal Communication",
        line: "Make internal messages land instead of getting skipped.",
    },
    {
        number: "04",
        name: "Brand Motion",
        line: "Give your brand a signature way of moving.",
    },
    {
        number: "05",
        name: "Motion Systems",
        line: "Reusable motion kits that scale across everything you ship.",
    },
];

const EASE = [0.25, 0.1, 0.25, 1];
const CARD_INITIAL = { opacity: 0, y: 60 };
const CARD_IN_VIEW = { opacity: 1, y: 0 };
const CARD_VIEWPORT = { once: true, margin: "50px" };
const CARD_HOVER = { scale: 1.03 };

export function ServicesSection() {
    return (
        <SectionFrame innerClassName="">
            <div className="py-20 md:py-28 px-5 md:px-10">
                <div className="mb-12 md:mb-16 px-1">
                    <div className="text-sm uppercase tracking-[0.3em] mb-4" style={{ color: "var(--accent)" }}>
                        SERVICES
                    </div>
                    <h2
                        data-testid={TID.servicesIntroTitle}
                        className="display-text font-black uppercase"
                        style={{
                            fontSize: "clamp(2rem, 6vw, 4.5rem)",
                            lineHeight: 1,
                            letterSpacing: "-0.03em",
                        }}
                    >
                        What we bring<br />to life
                    </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                    {SERVICES.map((svc, i) => (
                        <ServiceCard key={svc.number} svc={svc} index={i} preview={ASSETS.servicePreviews[i]} wide={i === 2} />
                    ))}
                </div>
            </div>
        </SectionFrame>
    );
}

function ServiceCard({ svc, index, preview, wide = false }) {
    const [hover, setHover] = useState(false);
    return (
        <motion.div
            initial={CARD_INITIAL}
            whileInView={CARD_IN_VIEW}
            viewport={CARD_VIEWPORT}
            transition={{
                duration: 0.7,
                delay: index * 0.12,
                ease: EASE,
            }}
            onHoverStart={() => setHover(true)}
            onHoverEnd={() => setHover(false)}
            whileHover={CARD_HOVER}
            className={`relative ${wide ? "md:col-span-2" : ""}`}
            data-testid={TID.serviceCard(index)}
        >
            <GlassCard className={`p-6 md:p-8 ${wide ? "h-[480px] md:h-[640px]" : "h-[440px] md:h-[560px]"} overflow-hidden`}>
                <div className="relative h-full flex flex-col">
                    <div
                        className="font-black"
                        style={{
                            color: "var(--muted)",
                            fontSize: "clamp(2rem, 5vw, 4rem)",
                            lineHeight: 1,
                        }}
                    >
                        {svc.number}
                    </div>
                    <h3
                        className="mt-4 font-medium uppercase display-text"
                        style={{
                            fontSize: "clamp(1.1rem, 2.4vw, 2rem)",
                            lineHeight: 1.05,
                            letterSpacing: "-0.01em",
                        }}
                    >
                        {svc.name}
                    </h3>

                    {/* Preview loop area — always visible */}
                    <div className={`mt-auto relative ${wide ? "h-[280px] md:h-[400px]" : "h-[240px] md:h-[320px]"}`}>
                        <AssetPlaceholder
                            caption={preview?.caption || "PREVIEW LOOP"}
                            video={preview?.video}
                            fit="contain"
                            className="h-full w-full"
                        >
                            {!preview?.video && <AnimatedPreviewBars />}
                        </AssetPlaceholder>
                    </div>

                    {/* Supporting line — hidden until hover */}
                    <motion.p
                        initial={false}
                        animate={{
                            opacity: hover ? 1 : 0,
                            y: hover ? 0 : 8,
                        }}
                        transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                        className="mt-4 text-base sm:text-base md:text-base font-light"
                        style={{ color: "var(--muted)" }}
                    >
                        {svc.line}
                    </motion.p>
                </div>
            </GlassCard>
        </motion.div>
    );
}

function AnimatedPreviewBars() {
    return (
        <div className="absolute inset-4 flex items-end justify-around gap-2">
            {Array.from({ length: 7 }).map((_, i) => (
                <motion.div
                    key={`bar-${i}`}
                    className="rounded-sm"
                    style={{
                        width: "8%",
                        background: "var(--accent)",
                        boxShadow: "0 0 10px var(--accent-soft)",
                    }}
                    animate={{
                        height: [`${20 + Math.random() * 30}%`, `${50 + Math.random() * 40}%`, `${20 + Math.random() * 30}%`],
                    }}
                    transition={{
                        duration: 1.4 + i * 0.1,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: i * 0.07,
                    }}
                />
            ))}
        </div>
    );
}
